import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ParticipantRole, ParticipantUser } from '@regigov/contracts';
import { usersApi } from '../api/selfservice';
import { useAuth } from '../auth/AuthContext';
import { Field, ProblemAlert, formatDateTime } from './ui';

/**
 * Participant user management. `participantId` is `me` on the participant
 * workbench and a participant id on the CER workbench.
 */
export function UsersPanel({ participantId, canManage }: { participantId: string; canManage: boolean }) {
  const { user, config } = useAuth();
  const qc = useQueryClient();
  const key = ['participants', participantId, 'users'];
  const users = useQuery({ queryKey: key, queryFn: () => usersApi.list(participantId) });
  const update = useMutation({
    mutationFn: (v: { id: string; role?: ParticipantRole; disabled?: boolean }) => usersApi.update(participantId, v.id, { role: v.role, disabled: v.disabled }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  if (users.isLoading) return <p className="muted">Loading…</p>;
  if (users.error || !users.data) return <ProblemAlert error={users.error ?? new Error('Not found')} />;
  const items = users.data.items;
  const admins = items.filter((u) => u.role === 'participant_admin' && !u.disabled).length;

  return (
    <>
      <p className="muted">
        Access is explicit: a user acts for this participant only through the role assigned here. <strong>Administrators</strong> maintain organisation details, register devices, transfer units and manage
        users; <strong>users</strong> can view records, register devices and report incidents.
      </p>
      <ProblemAlert error={update.error} />
      <table className="table">
        <thead>
          <tr>
            <th>User</th>
            <th>Sign-in</th>
            <th>Role</th>
            <th>Status</th>
            <th>Added</th>
            {canManage && <th />}
          </tr>
        </thead>
        <tbody>
          {items.map((u) => {
            const isSelf = u.id === user?.id;
            const lastAdmin = u.role === 'participant_admin' && !u.disabled && admins <= 1;
            return (
              <tr key={u.id} className={u.disabled ? 'muted' : undefined}>
                <td>
                  {u.displayName} {isSelf && <span className="muted">(you)</span>}
                  <br />
                  <code className="muted">{u.username}</code>
                  {u.email && (
                    <>
                      <br />
                      <span className="muted">
                        {u.email} {u.emailVerified ? '(verified)' : '(not verified)'}
                      </span>
                    </>
                  )}
                </td>
                <td>{u.provider === 'entra' ? 'Entra ID' : 'Password'}</td>
                <td>{u.role === 'participant_admin' ? 'Administrator' : 'User'}</td>
                <td>{u.disabled ? 'Disabled' : 'Active'}</td>
                <td>{formatDateTime(u.createdAt)}</td>
                {canManage && (
                  <td>
                    {!isSelf && !u.disabled && (
                      <>
                        <button
                          type="button"
                          className="linklike"
                          disabled={update.isPending || lastAdmin}
                          title={lastAdmin ? 'The last active administrator cannot be demoted' : undefined}
                          onClick={() => update.mutate({ id: u.id, role: u.role === 'participant_admin' ? 'participant_user' : 'participant_admin' })}
                        >
                          {u.role === 'participant_admin' ? 'Make user' : 'Make administrator'}
                        </button>{' '}
                        ·{' '}
                        <button
                          type="button"
                          className="linklike"
                          disabled={update.isPending || lastAdmin}
                          title={lastAdmin ? 'The last active administrator cannot be disabled' : undefined}
                          onClick={() => {
                            if (window.confirm(`Disable ${u.displayName}? They will be signed out and unable to sign in.`)) update.mutate({ id: u.id, disabled: true });
                          }}
                        >
                          Disable
                        </button>
                      </>
                    )}
                    {!isSelf && u.disabled && (
                      <button type="button" className="linklike" disabled={update.isPending} onClick={() => update.mutate({ id: u.id, disabled: false })}>
                        Re-enable
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {canManage && <InviteForm participantId={participantId} entra={config?.provider === 'entra'} onInvited={() => void qc.invalidateQueries({ queryKey: key })} />}
    </>
  );
}

function InviteForm({ participantId, entra, onInvited }: { participantId: string; entra: boolean; onInvited: (u: ParticipantUser) => void }) {
  const [open, setOpen] = useState(false);
  const empty = { username: '', displayName: '', role: 'participant_user' as ParticipantRole, password: '', email: '', requireEmailVerification: true };
  const [values, setValues] = useState(empty);
  const invite = useMutation({
    mutationFn: () =>
      usersApi.invite(participantId, {
        username: values.username.trim(),
        displayName: values.displayName.trim(),
        role: values.role,
        ...(!entra && { password: values.password }),
        ...(!entra && values.email.trim() && { email: values.email.trim(), requireEmailVerification: values.requireEmailVerification }),
      }),
    onSuccess: (u) => {
      setOpen(false);
      setValues(empty);
      onInvited(u);
    },
  });
  const set = (k: Exclude<keyof typeof values, 'requireEmailVerification'>) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [k]: e.target.value }));

  if (!open) {
    return (
      <div>
        <button type="button" onClick={() => setOpen(true)}>
          Invite a user
        </button>
      </div>
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    invite.mutate();
  }

  return (
    <form className="form card" onSubmit={onSubmit} noValidate>
      <h2>Invite a user</h2>
      <Field label={entra ? 'Work email (Entra sign-in)' : 'Username'} hint={entra ? 'The account is linked the first time they sign in with Entra ID' : 'Letters, digits and . _ @ + - only'}>
        <input value={values.username} onChange={set('username')} required disabled={invite.isPending} autoComplete="off" />
      </Field>
      <Field label="Display name">
        <input value={values.displayName} onChange={set('displayName')} required disabled={invite.isPending} />
      </Field>
      <Field label="Role">
        <select value={values.role} onChange={set('role')} disabled={invite.isPending}>
          <option value="participant_user">User</option>
          <option value="participant_admin">Administrator</option>
        </select>
      </Field>
      {!entra && (
        <Field label="Initial password" hint="At least 12 characters; ask them to change it after first sign-in">
          <input type="password" value={values.password} onChange={set('password')} required minLength={12} disabled={invite.isPending} autoComplete="new-password" />
        </Field>
      )}
      {!entra && (
        <>
          <Field label="Email address (optional)" hint="Used for verification and, later, password resets">
            <input type="email" value={values.email} onChange={set('email')} disabled={invite.isPending} autoComplete="off" />
          </Field>
          {values.email.trim() && (
            <label className="checkbox">
              <input
                type="checkbox"
                checked={values.requireEmailVerification}
                onChange={(e) => setValues((v) => ({ ...v, requireEmailVerification: e.target.checked }))}
                disabled={invite.isPending}
              />{' '}
              Requires email verification: we email them a 24-hour link and they can't sign in until they open it. Untick to record the address as verified.
            </label>
          )}
        </>
      )}
      <ProblemAlert error={invite.error} />
      <div className="row">
        <button type="submit" disabled={invite.isPending}>
          Invite
        </button>
        <button type="button" className="secondary" onClick={() => setOpen(false)} disabled={invite.isPending}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function ChangePasswordForm() {
  const { config } = useAuth();
  const [values, setValues] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [done, setDone] = useState(false);
  const change = useMutation({
    mutationFn: async () => {
      if (values.newPassword !== values.confirm) throw new Error('New passwords do not match');
      await usersApi.changePassword({ currentPassword: values.currentPassword, newPassword: values.newPassword });
    },
    onSuccess: () => {
      setValues({ currentPassword: '', newPassword: '', confirm: '' });
      setDone(true);
    },
  });
  const set = (k: keyof typeof values) => (e: { target: { value: string } }) => {
    setDone(false);
    setValues((v) => ({ ...v, [k]: e.target.value }));
  };

  if (config?.provider === 'entra') return <p className="muted">Your password is managed by your organisation's Entra ID tenant.</p>;

  return (
    <form
      className="form card"
      onSubmit={(e) => {
        e.preventDefault();
        change.mutate();
      }}
      noValidate
    >
      <h2>Change password</h2>
      <Field label="Current password">
        <input type="password" value={values.currentPassword} onChange={set('currentPassword')} required autoComplete="current-password" disabled={change.isPending} />
      </Field>
      <Field label="New password" hint="At least 12 characters">
        <input type="password" value={values.newPassword} onChange={set('newPassword')} required minLength={12} autoComplete="new-password" disabled={change.isPending} />
      </Field>
      <Field label="Confirm new password">
        <input type="password" value={values.confirm} onChange={set('confirm')} required autoComplete="new-password" disabled={change.isPending} />
      </Field>
      <ProblemAlert error={change.error} />
      {done && <p role="status">Password changed.</p>}
      <div>
        <button type="submit" disabled={change.isPending}>
          Change password
        </button>
      </div>
    </form>
  );
}
